import {
  Box,
  Typography,
  Chip,
  IconButton,
  LinearProgress,
  useTheme,
} from "@mui/material";
import {
  CheckCircleOutline,
  DownloadOutlined,
  PrintOutlined,
  ErrorOutline,
} from "@mui/icons-material";
import { tokens } from "../../../../theme";
import type { IEquipment } from "../../../../types/equipment";
import Button from "../../../../components/common/Button";

interface Props {
  equipment: IEquipment;
}

export default function OverviewTab({ equipment }: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* TOP ROW */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
          gap: 3,
        }}
      >
        {/* Finance Card */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={3}>
            Finance
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: 2,
              mb: 4,
            }}
          >
            {[
              {
                label: "Purchase cost",
                value: `$${equipment.purchaseCost || 999}`,
                bg: colors.neutral[200],
              },
              {
                label: "Rental Income",
                value: "$450",
                bg: colors.secondary[500],
              },
              {
                label: "ROI",
                value: "20%",
                bg: colors.primary[600],
              },
              {
                label: "Maint.Cost",
                value: "$450",
                bg: colors.primary[800],
              },
            ].map((stat, index) => (
              <Box
                key={index}
                sx={{
                  bgcolor: stat.bg,
                  borderRadius: "8px",
                  p: 2,
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color={colors.primary[700]}
                >
                  {stat.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Cost Recovery
            </Typography>
            <Typography variant="body2" color="text.primary" fontWeight={500}>
              Recovered 20% of cost via rentals
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={20}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: colors.primary[800],
              "& .MuiLinearProgress-bar": {
                backgroundColor: colors.secondary[10],
                borderRadius: 5,
              },
            }}
          />
        </Box>

        {/* Rental Snapshot Card */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={2}>
            Rental Snapshot
          </Typography>
          <Box
            sx={{
              border: "1px solid #f8bbd0",
              bgcolor: "#fffafb",
              borderRadius: "10px",
              p: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="text.primary"
                >
                  Event House Media
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Return: 27 Mar, 2026
                </Typography>
              </Box>
              <Chip
                label="Overdue"
                size="small"
                sx={{
                  bgcolor: "#ffebee",
                  color: colors.neutral[10],
                  fontWeight: 600,
                  height: 24,
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 3,
              }}
            >
              <ErrorOutline sx={{ color: "#ed6c02", fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary">
                overdue - action required
              </Typography>
            </Box>
            <Button
              fullWidth
              variant="contained"
              startIcon={<CheckCircleOutline />}
              sx={{
                bgcolor: colors.primary[400],
                color: colors.primary[900],
                textTransform: "none",
                py: 1.5,
                borderRadius: "8px",
                "&:hover": { bgcolor: colors.primary[400] },
              }}
            >
              Mark as returned
            </Button>
          </Box>
          <Typography
            variant="body2"
            sx={{
              mt: 2,
              color: colors.primary[400],
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            View Full Rental History
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 3,
        }}
      >
        {/* Physical Specs */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={3}>
            Physical Specs
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {[
              { label: "Length", value: `${equipment.length || 100} cm` },
              { label: "Height", value: `${equipment.height || 80} cm` },
              { label: "Weight", value: `${equipment.weight || 20} kg` },
              { label: "Current", value: `${equipment.current || 15} A` },
              { label: "Power", value: `${equipment.power || 100} w` },
            ].map((item, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color="text.primary"
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Identification */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={3}>
            Identification
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                bgcolor: colors.primary[800],
                borderRadius: "8px",
                display: "grid",
                placeItems: "center",
                border: `1px solid ${colors.primary[800]}`,
              }}
            >
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${equipment.serialNo || "SN-123"}`}
                alt="QR"
                style={{ width: "90%", height: "90%" }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                sx={{
                  border: `1px solid ${colors.primary[400]}`,
                  borderRadius: "8px",
                  color: colors.primary[400],
                }}
              >
                <DownloadOutlined  />
              </IconButton>
              <IconButton
                sx={{
                  border: `1px solid ${colors.primary[400]}`,
                  borderRadius: "8px",
                  color: "text.secondary",
                }}
              >
                <PrintOutlined sx={{color: colors.primary[400]}}/>
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                Serial Number
              </Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {equipment.serialNo || "SN-CHV-42816-25840"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                Location
              </Typography>
              <Typography variant="body2" fontWeight={500} color="text.primary">
                {equipment.location || "Gourmet Garage"}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Supplier */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={3}>
            Supplier
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {[
              { label: "Supplier", value: equipment.supplierName || "CineTech" },
              { label: "Contact", value: equipment.contactName || "James Moyo" },
              { label: "Phone", value: equipment.supplierPhone || "+263 77 123 4567" },
              { label: "Email", value: equipment.supplierEmail || "james@cineech.co.zw" },
              {
                label: "Cost",
                value: equipment.purchaseCost ? `$${equipment.purchaseCost}` : "$1,200",
              },
            ].map((item, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography variant="body2" fontWeight={500} color="text.primary">
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* BOTTOM ROW */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 3,
        }}
      >
        {/* Utilization */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={2}>
            Utilization
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 2 }}>
            <Typography variant="h3" fontWeight={700} color="text.primary">
              15
            </Typography>
            <Typography variant="body2" color="text.secondary">
              days this month
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={50}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: colors.primary[800],
              "& .MuiLinearProgress-bar": {
                backgroundColor: colors.neutral[300],
                borderRadius: 4,
              },
            }}
          />
        </Box>

        {/* Health Score */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={2}>
            Health Score
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mb: 1 }}>
            <Typography variant="h3" fontWeight={700} color="text.primary">
              95
            </Typography>
            <Typography variant="body1" color="text.secondary" fontWeight={500}>
              /100
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Based on maintenance frequency and condition
          </Typography>
        </Box>

        {/* Current Allocation */}
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={2}>
            Current Allocation
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}